import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalAcademicYear } from '../../contexts/AcademicYearContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  KeyRound,
  LogOut,
  UserCog,
  UserRoundCheck,
  Users,
} from 'lucide-react';
import { navigationModules, roleLabels, isItemActive, buildStudentPortalModule } from './navigationConfig';
import { AlertBell } from './AlertBell';
import { ChangePasswordDialog } from './ChangePasswordDialog';

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const {
    academicYears,
    globalAcademicYearId,
    loading: loadingAcademicYears,
    setGlobalAcademicYearId,
    termLevelFilter,
    setTermLevelFilter,
    levels,
  } = useGlobalAcademicYear();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const yearLevels = levels.filter(
    (level) => String(level.academic_year_id) === String(globalAcademicYearId) && level.is_active !== false,
  );

  // Drop a stale level selection when the chosen year has no level by that name
  useEffect(() => {
    if (!termLevelFilter || levels.length === 0) return;
    if (!yearLevels.some((level) => level.name === termLevelFilter)) {
      setTermLevelFilter('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAcademicYearId, levels.length]);

  const visibleModules = navigationModules
    .filter((module) => {
      if (!module.roles.includes(user?.role)) return false;
      // CRMI is only relevant for students/parents enrolled in a CRMI level
      if (module.id === 'crmi' && (user?.role === 'student' || user?.role === 'parent')) {
        return user?.is_crmi === true;
      }
      // Electives belong to elective-eligible levels (3rd/4th year)
      if (module.id === 'electives' && user?.role === 'student') {
        return user?.is_elective_eligible === true;
      }
      // Clinical rotations are independent of electives: the menu shows
      // only when the student is in a published rotation plan (any level)
      if (module.id === 'prac_rotation' && user?.role === 'student') {
        return user?.has_rotation === true;
      }
      // PG module is only relevant for students enrolled in a PG level
      if (module.id === 'pg' && user?.role === 'student') {
        return user?.is_pg === true;
      }
      return true;
    })
    .map((module) => ({
      ...module,
      items: module.items.filter(
        (item) => item.roles.includes(user?.role) && (!item.isVisible || item.isVisible(user)),
      ),
    }))
    .filter((module) => module.items.length > 0 || (module.comingSoon && module.comingSoon.length > 0));

  const studentPortalModule = buildStudentPortalModule(user);

  const allModules = [...(studentPortalModule ? [studentPortalModule] : []), ...visibleModules];
  const activeModule = allModules.find((module) => module.items.some((item) => isItemActive(location.pathname, item)));
  const activeModuleLabel = activeModule?.label || 'Home';
  const activeItem = allModules.flatMap((m) => m.items).find((item) => isItemActive(location.pathname, item));
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background" data-testid="dashboard-layout">
      <aside
        className={`${collapsed ? 'w-16' : 'w-72'} bg-sidebar border-r border-sidebar-border text-sidebar-fg flex flex-col transition-all duration-200 shadow-[10px_0_30px_rgba(16,30,24,0.12)]`}
        data-testid="dashboard-sidebar"
      >
        <div className="h-16 flex items-center px-4 border-b border-sidebar-border shrink-0 bg-sidebar-header overflow-hidden">
          {collapsed ? (
            /* Collapsed: icon mark only */
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 44 44" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <clipPath id="hbc">
                    <rect x="8" y="19" width="28" height="6" rx="3" />
                  </clipPath>
                </defs>
                <rect x="19" y="8" width="6" height="28" rx="3" fill="white" />
                <rect x="8" y="19" width="28" height="6" rx="3" fill="white" />
                <polyline
                  points="8,22 11,22 13,19 15.5,25 18,22 19,22"
                  clipPath="url(#hbc)"
                  stroke="rgba(255,255,255,0.35)"
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : (
            /* Expanded: full logo lockup via SVG */
            <img
              src="/logo-white.svg"
              alt="CliniQ 360"
              className="h-9 w-auto select-none"
              draggable={false}
            />
          )}
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className={cn('space-y-5 px-2', collapsed && 'space-y-2')}>
            {allModules.map((module) => (
              <section key={module.id} className="space-y-1">
                {!collapsed ? (
                  <div className="px-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sidebar-fg">{module.label}</p>
                    <p className="text-xs text-sidebar-muted mt-0.5">{module.subtitle}</p>
                  </div>
                ) : null}

                <div className="space-y-1">
                  {module.items.map((item) => {
                    const isActive = isItemActive(location.pathname, item);
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200',
                          isActive
                            ? 'bg-sidebar-active-bg text-sidebar-active-fg font-medium shadow-sm'
                            : 'text-sidebar-fg hover:bg-white/10 hover:text-white',
                        )}
                        data-testid={item.testId}
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed ? <span>{item.label}</span> : null}
                      </button>
                    );
                  })}
                </div>

                {/* {!collapsed && module.comingSoon?.length ? (
                  <div className="mx-3 mt-2 rounded-xl border border-dashed border-white/15 bg-white/5 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-fg">Coming Soon</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {module.comingSoon.map((item) => (
                        <span
                          key={item}
                          className="inline-flex items-center rounded-full bg-white/8 px-2 py-1 text-[11px] text-sidebar-fg border border-white/10"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null} */}
              </section>
            ))}
          </nav>
        </ScrollArea>

        <div className="border-t border-sidebar-border p-3 space-y-2 shrink-0 bg-sidebar-header">
          {!collapsed && user ? (
            <div className="px-2 mb-2">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-sidebar-muted">{roleLabels[user.role] || user.role}</p>
            </div>
          ) : null}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="text-sidebar-fg hover:bg-white/10 hover:text-white"
              data-testid="sidebar-collapse-btn"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowChangePassword(true)}
              className="text-sidebar-fg hover:bg-white/10 hover:text-white"
              title="Change Password"
              data-testid="change-password-btn"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-sidebar-fg hover:bg-white/10 hover:text-red-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-10 border-b border-border bg-white/96 backdrop-blur shadow-sm">
          <div className="px-6 py-3 max-w-[1600px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 min-w-0 text-sm">
              <span className="text-muted-foreground font-medium shrink-0">{activeModuleLabel}</span>
              {activeItem && activeItem.label !== activeModuleLabel && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-foreground font-medium truncate">{activeItem.label}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* ── Level filter — levels of the globally selected year ── */}
              {(['super_admin', 'tenant_admin', 'faculty'].includes(user?.role)) && yearLevels.length > 0 && (
                <Select
                  value={termLevelFilter || 'all'}
                  onValueChange={(v) => setTermLevelFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-44 bg-white" data-testid="term-level-filter-select">
                    <SelectValue placeholder="All Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    {yearLevels.map((level) => (
                      <SelectItem key={level.id} value={level.name}>{level.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Global academic year select — shown for all roles */}
              {academicYears.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground hidden sm:inline">Year:</span>
                  <Select
                    value={globalAcademicYearId || undefined}
                    onValueChange={setGlobalAcademicYearId}
                    disabled={loadingAcademicYears || academicYears.length === 0}
                  >
                    <SelectTrigger className="w-44 bg-white" data-testid="layout-academic-year-select">
                      <SelectValue placeholder="Academic Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {academicYears.map((year) => (
                        <SelectItem key={year.id} value={String(year.id)}>
                          {year.label}{year.is_active ? ' (Active)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}


              {['super_admin', 'tenant_admin'].includes(user?.role) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="bg-white border-border" title="User Management" data-testid="people-menu-btn">
                      <UserCog className="w-4 h-4 text-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">User Management</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard/users/staff')}>
                      <Users className="w-4 h-4 mr-2" /> Staff &amp; Admins
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/users/faculty')}>
                      <GraduationCap className="w-4 h-4 mr-2" /> Faculty
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/students')}>
                      <BookOpen className="w-4 h-4 mr-2" /> Students
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/users/parents')}>
                      <UserRoundCheck className="w-4 h-4 mr-2" /> Parents
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <AlertBell user={user} globalAcademicYearId={globalAcademicYearId} />
            </div>
          </div>
        </div>
        <div className="p-6 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
